import { calculatePurchaseBaseQuantity, calculatePurchaseDetailSubtotal, isBaseQuantityUnit } from "../../../utils/helpers/purchaseCalculations";
import { getBaseUnitValue, getUnitFactorToBase } from "../../../utils/helpers/pricing";

const numeric = (value) => Number(value || 0);
const rounded = (value, precision = 6) => Number(value.toFixed(precision));
const exceedsStockPrecision = (value) => Math.abs(numeric(value) - rounded(numeric(value), 4)) > 0.00000001;
const priceMultiplier = (unit, units) => isBaseQuantityUnit(unit, units) ? 1000 : 1;

export const getPurchaseUnitOptions = (materialUnit, units, currentUnit) => {
  const base = getBaseUnitValue(units, materialUnit);
  const options = units.filter((unit) => getBaseUnitValue(units, unit.value) === base);
  if (currentUnit && !options.some((unit) => unit.value === currentUnit) && getBaseUnitValue(units, currentUnit) === base) {
    options.push({ value: currentUnit, label: currentUnit });
  }
  return options;
};

// Existing purchases keep their stock and price meaning when opened for editing.
export const getPurchaseItemInput = (detail, units) => {
  if (detail.purchase_input) return detail.purchase_input;
  const weighted = isBaseQuantityUnit(detail.unit, units);
  const packaged = weighted && numeric(detail.qty) > 1 && Number.isInteger(numeric(detail.qty));
  const size = numeric(detail.weight);
  const factor = getUnitFactorToBase(units, detail.unit);
  return {
    packaged,
    unit: detail.material_id ? detail.unit : "",
    quantity: detail.material_id ? (packaged || !weighted ? detail.qty : rounded(size * numeric(detail.qty))) : "",
    package_size: size || 1,
    price: detail.unit_cost === "" || detail.unit_cost == null ? "" : rounded(numeric(detail.unit_cost) * factor * (packaged ? size : 1) / priceMultiplier(detail.unit, units)),
  };
};

export const createPurchaseItemInput = (material, units) => {
  const options = getPurchaseUnitOptions(material.unit, units);
  const preferred = { gram: "kg", ml: "l" }[getBaseUnitValue(units, material.unit)];
  const unit = options.find((option) => option.value === preferred)?.value || material.unit;
  const factor = getUnitFactorToBase(units, unit);
  const size = numeric(material.weight) * getUnitFactorToBase(units, material.unit) / factor;
  return {
    packaged: false,
    unit,
    quantity: "",
    package_size: size > 0 ? rounded(size) : 1,
    price: rounded(numeric(material.unit_cost) * factor / priceMultiplier(unit, units)),
  };
};

export const changePurchaseInputUnit = (input, unit, units) => {
  const ratio = getUnitFactorToBase(units, input.unit) / getUnitFactorToBase(units, unit);
  return {
    ...input,
    unit,
    quantity: input.packaged || input.quantity === "" ? input.quantity : rounded(numeric(input.quantity) * ratio),
    package_size: input.package_size === "" ? "" : rounded(numeric(input.package_size) * ratio),
    price: input.packaged || input.price === "" ? input.price : rounded(numeric(input.price) / ratio),
  };
};

export const togglePurchasePackaging = (input) => {
  let size = numeric(input.package_size) || 1;
  if (!input.packaged) {
    // Do not invent fractional packages when changing the input mode.
    const count = numeric(input.quantity) / size;
    if (input.quantity !== "" && (!Number.isInteger(count) || count < 1)) size = numeric(input.quantity) || 1;
  }
  return {
    ...input,
    packaged: !input.packaged,
    package_size: size,
    quantity: input.quantity === "" ? "" : rounded(input.packaged ? numeric(input.quantity) * size : numeric(input.quantity) / size),
    price: input.price === "" ? "" : rounded(input.packaged ? numeric(input.price) / size : numeric(input.price) * size),
  };
};

export const applyPurchaseItemInput = (detail, input, units) => {
  const factor = getUnitFactorToBase(units, input.unit);
  const size = input.packaged ? numeric(input.package_size) : 1;
  const weighted = isBaseQuantityUnit(input.unit, units);
  // The API still uses its canonical price per kg/liter or per base count unit.
  const updated = {
    ...detail,
    purchase_input: input,
    unit: input.unit,
    weight: weighted ? rounded(input.packaged ? size : numeric(input.quantity), 4) : 0,
    qty: weighted ? (input.packaged ? numeric(input.quantity) : 1) : rounded(numeric(input.quantity) * size, 4),
    unit_cost: input.price === "" ? "" : (size > 0 ? rounded(numeric(input.price) * priceMultiplier(input.unit, units) / (size * factor)) : 0),
  };
  return { ...updated, sub_total: calculatePurchaseDetailSubtotal(updated, units) };
};

export const purchaseItemErrors = (detail, units, materialUnit) => {
  const input = getPurchaseItemInput(detail, units);
  const baseQuantity = calculatePurchaseBaseQuantity(detail, units);
  return {
    item: !detail.material_id ? "Material wajib dipilih." : "",
    unit: !input.unit ? "Pilih satuan pembelian." : materialUnit && getBaseUnitValue(units, materialUnit) !== getBaseUnitValue(units, input.unit) ? "Satuan harus sesuai dengan material." : "",
    quantity: !Number.isFinite(Number(input.quantity)) || numeric(input.quantity) <= 0 ? "Jumlah dibeli harus lebih dari 0." : input.packaged && !Number.isInteger(numeric(input.quantity)) ? "Jumlah kemasan harus berupa bilangan bulat." : exceedsStockPrecision(input.quantity) ? "Gunakan maksimal 4 angka di belakang koma." : baseQuantity < 0.0001 ? "Jumlah terlalu kecil untuk dicatat sebagai stok." : "",
    package_size: input.packaged && (!Number.isFinite(Number(input.package_size)) || numeric(input.package_size) <= 0) ? "Isi setiap kemasan harus lebih dari 0." : input.packaged && exceedsStockPrecision(input.package_size) ? "Gunakan maksimal 4 angka di belakang koma." : "",
    price: input.price === "" || input.price == null || !Number.isFinite(Number(input.price)) || numeric(input.price) < 0 ? "Harga wajib diisi dan tidak boleh negatif." : "",
  };
};
